import {
  IDoesFilterPassParams,
  IFilterComp,
  IFilterParams,
  IRowNode,
} from '@ag-grid-community/core';

type TextMatchType =
  | 'contains'
  | 'notContains'
  | 'equals'
  | 'notEqual'
  | 'startsWith'
  | 'endsWith';

type RuleGroupOperator = 'AND' | 'OR';

type FilterRule = {
  id: number;
  matchType: TextMatchType;
  value: string;
};

type RulesOrPicklistFilterModel = {
  rules?: Array<{ matchType: TextMatchType; value: string }>;
  rulesOperator?: RuleGroupOperator;
  textValues?: string[];
  textRaw?: string;
  matchType?: TextMatchType;
  options?: string[];
  text?: string;
};

type AgTextConditionModel = {
  type?: string;
  filter?: string;
};

type AgTextFilterModel = {
  filterType?: string;
  type?: string;
  filter?: string;
  operator?: 'AND' | 'OR';
  conditions?: AgTextConditionModel[];
  condition1?: AgTextConditionModel;
  condition2?: AgTextConditionModel;
};

const MATCH_OPTIONS: Array<{ value: TextMatchType; label: string }> = [
  { value: 'contains', label: 'Contains' },
  { value: 'notContains', label: 'Does not contain' },
  { value: 'equals', label: 'Equals' },
  { value: 'notEqual', label: 'Does not equal' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
];

export class RulesOrPicklistFilter implements IFilterComp {
  private eGui!: HTMLDivElement;
  private rulesContainer!: HTMLDivElement;
  private rulesOperatorSelect!: HTMLSelectElement;
  private addRuleButton!: HTMLButtonElement;
  private optionsContainer!: HTMLDivElement;

  private filterParams!: IFilterParams;
  private filterChangedCallback!: () => void;
  private fieldName = '';
  private refreshOptionsListener = () => {
    this.renderOptions();
  };

  private rules: FilterRule[] = [];
  private rulesOperator: RuleGroupOperator = 'AND';
  private nextRuleId = 1;
  private selectedOptions = new Set<string>();

  init(params: IFilterParams) {
    this.filterParams = params;
    this.filterChangedCallback = params.filterChangedCallback;
    this.fieldName = String(params.colDef.field ?? '');

    this.rules = [this.createRule()];

    this.eGui = document.createElement('div');
    this.eGui.style.padding = '12px';
    this.eGui.style.minWidth = '300px';
    this.eGui.style.display = 'flex';
    this.eGui.style.flexDirection = 'column';
    this.eGui.style.gap = '8px';

    const customRulesSection = document.createElement('div');
    const customRulesLabel = document.createElement('label');
    customRulesLabel.textContent = 'Custom Text Rules:';
    customRulesLabel.style.fontWeight = 'bold';
    customRulesLabel.style.display = 'block';
    customRulesLabel.style.marginBottom = '4px';
    customRulesLabel.style.fontSize = '12px';

    const operatorLabel = document.createElement('label');
    operatorLabel.textContent = 'Rules operator:';
    operatorLabel.style.display = 'block';
    operatorLabel.style.marginBottom = '4px';
    operatorLabel.style.fontSize = '12px';

    this.rulesOperatorSelect = document.createElement('select');
    this.rulesOperatorSelect.className =
      'ag-input-field-input ag-text-field-input';
    this.rulesOperatorSelect.style.width = '100%';
    this.rulesOperatorSelect.style.padding = '6px';
    this.rulesOperatorSelect.style.marginBottom = '8px';

    ['AND', 'OR'].forEach((operator) => {
      const optionEl = document.createElement('option');
      optionEl.value = operator;
      optionEl.text = operator;
      this.rulesOperatorSelect.appendChild(optionEl);
    });

    this.rulesOperatorSelect.value = this.rulesOperator;
    this.rulesOperatorSelect.addEventListener('change', (event) => {
      this.rulesOperator = (event.target as HTMLSelectElement)
        .value as RuleGroupOperator;
      this.filterChangedCallback();
    });

    this.rulesContainer = document.createElement('div');
    this.rulesContainer.style.display = 'flex';
    this.rulesContainer.style.flexDirection = 'column';
    this.rulesContainer.style.gap = '6px';

    this.addRuleButton = document.createElement('button');
    this.addRuleButton.type = 'button';
    this.addRuleButton.textContent = '+ Add rule';
    this.addRuleButton.className = 'ag-standard-button';
    this.addRuleButton.style.marginTop = '6px';
    this.addRuleButton.style.padding = '6px';
    this.addRuleButton.style.cursor = 'pointer';
    this.addRuleButton.addEventListener('click', () => {
      this.rules.push(this.createRule());
      this.renderRules();
    });

    customRulesSection.appendChild(customRulesLabel);
    customRulesSection.appendChild(operatorLabel);
    customRulesSection.appendChild(this.rulesOperatorSelect);
    customRulesSection.appendChild(this.rulesContainer);
    customRulesSection.appendChild(this.addRuleButton);

    const divider = document.createElement('hr');
    divider.style.border = '0';
    divider.style.borderTop = '1px solid var(--ag-border-color, #eee)';
    divider.style.margin = '4px 0';

    const picklistSection = document.createElement('div');
    const picklistLabel = document.createElement('label');
    picklistLabel.textContent = 'Current Grid Options:';
    picklistLabel.style.fontWeight = 'bold';
    picklistLabel.style.display = 'block';
    picklistLabel.style.marginBottom = '4px';
    picklistLabel.style.fontSize = '12px';

    this.optionsContainer = document.createElement('div');
    this.optionsContainer.style.maxHeight = '140px';
    this.optionsContainer.style.overflowY = 'auto';
    this.optionsContainer.style.display = 'flex';
    this.optionsContainer.style.flexDirection = 'column';
    this.optionsContainer.style.gap = '6px';

    this.optionsContainer.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement;
      if (target.type !== 'checkbox') {
        return;
      }

      const optionValue = target.value;
      if (target.checked) {
        this.selectedOptions.add(optionValue);
      } else {
        this.selectedOptions.delete(optionValue);
      }

      this.filterChangedCallback();
    });

    picklistSection.appendChild(picklistLabel);
    picklistSection.appendChild(this.optionsContainer);

    this.eGui.appendChild(customRulesSection);
    this.eGui.appendChild(divider);
    this.eGui.appendChild(picklistSection);

    this.renderRules();
    this.renderOptions();

    params.api.addEventListener('rowDataUpdated', this.refreshOptionsListener);
    params.api.addEventListener('modelUpdated', this.refreshOptionsListener);
    params.api.addEventListener(
      'firstDataRendered',
      this.refreshOptionsListener,
    );
  }

  destroy() {
    this.filterParams.api.removeEventListener(
      'rowDataUpdated',
      this.refreshOptionsListener,
    );
    this.filterParams.api.removeEventListener(
      'modelUpdated',
      this.refreshOptionsListener,
    );
    this.filterParams.api.removeEventListener(
      'firstDataRendered',
      this.refreshOptionsListener,
    );
  }

  getGui() {
    return this.eGui;
  }

  afterGuiAttached() {
    this.renderOptions();
  }

  onNewRowsLoaded() {
    this.renderOptions();
  }

  isFilterActive() {
    return this.getActiveRules().length > 0 || this.selectedOptions.size > 0;
  }

  doesFilterPass(params: IDoesFilterPassParams) {
    const rawCellValue = this.getCellValue(params.node);
    const normalizedCellValue = rawCellValue.toLowerCase();
    const activeRules = this.getActiveRules();

    const hasText = activeRules.length > 0;
    const hasPicklist = this.selectedOptions.size > 0;

    const matchesText =
      hasText &&
      this.matchesRuleGroup(normalizedCellValue, rawCellValue, activeRules);
    const matchesPicklist =
      hasPicklist && this.selectedOptions.has(rawCellValue);

    if (hasText && hasPicklist) {
      return matchesText || matchesPicklist;
    }

    if (hasText) {
      return matchesText;
    }

    if (hasPicklist) {
      return matchesPicklist;
    }

    return true;
  }

  getModel() {
    if (!this.isFilterActive()) {
      return null;
    }

    return {
      rules: this.getActiveRules().map((rule) => ({
        matchType: rule.matchType,
        value: rule.value.trim(),
      })),
      rulesOperator: this.rulesOperator,
      options: Array.from(this.selectedOptions),
    };
  }

  setModel(model: RulesOrPicklistFilterModel | null) {
    if (!model) {
      this.rules = [this.createRule()];
      this.rulesOperator = 'AND';
      this.selectedOptions = new Set<string>();

      this.rulesOperatorSelect.value = this.rulesOperator;
      this.renderRules();
      this.renderOptions();
      return;
    }

    const { rules, rulesOperator } = this.deserializeRules(model);
    this.rulesOperator = rulesOperator;
    this.rulesOperatorSelect.value = this.rulesOperator;

    this.rules = rules.length > 0 ? rules : [this.createRule()];
    this.selectedOptions = new Set(model.options ?? []);

    this.renderRules();
    this.renderOptions();
  }

  private createRule(
    matchType: TextMatchType = 'contains',
    value = '',
  ): FilterRule {
    const rule = {
      id: this.nextRuleId,
      matchType,
      value,
    };

    this.nextRuleId += 1;
    return rule;
  }

  private getActiveRules(): FilterRule[] {
    return this.rules.filter((rule) => rule.value.trim().length > 0);
  }

  private deserializeRules(model: RulesOrPicklistFilterModel): {
    rules: FilterRule[];
    rulesOperator: RuleGroupOperator;
  } {
    const agTextModel = this.deserializeAgTextFilterModel(
      model as RulesOrPicklistFilterModel & AgTextFilterModel,
    );
    if (agTextModel.rules.length > 0) {
      return agTextModel;
    }

    if (model.rules && model.rules.length > 0) {
      return {
        rules: model.rules.map((rule) =>
          this.createRule(rule.matchType, rule.value),
        ),
        rulesOperator: model.rulesOperator ?? 'AND',
      };
    }

    const legacyMatchType = model.matchType ?? 'contains';
    const legacyValues =
      model.textValues && model.textValues.length > 0
        ? model.textValues
        : this.parseLegacyRawText(model.textRaw ?? model.text ?? '');

    return {
      rules: legacyValues.map((value) =>
        this.createRule(legacyMatchType, value),
      ),
      rulesOperator: model.rulesOperator ?? 'AND',
    };
  }

  private deserializeAgTextFilterModel(
    model: RulesOrPicklistFilterModel & AgTextFilterModel,
  ): { rules: FilterRule[]; rulesOperator: RuleGroupOperator } {
    if (model.filterType !== 'text') {
      return { rules: [], rulesOperator: 'AND' };
    }

    const resultRules: FilterRule[] = [];

    const pushCondition = (condition?: AgTextConditionModel) => {
      if (!condition) {
        return;
      }

      const ruleType = this.mapAgTextTypeToRuleType(condition.type);
      const ruleValue = (condition.filter ?? '').trim();

      if (!ruleType || ruleValue.length === 0) {
        return;
      }

      resultRules.push(this.createRule(ruleType, ruleValue));
    };

    if (Array.isArray(model.conditions) && model.conditions.length > 0) {
      model.conditions.forEach((condition) => pushCondition(condition));
      return {
        rules: resultRules,
        rulesOperator: model.operator ?? 'AND',
      };
    }

    if (model.condition1 || model.condition2) {
      pushCondition(model.condition1);
      pushCondition(model.condition2);
      return {
        rules: resultRules,
        rulesOperator: model.operator ?? 'AND',
      };
    }

    pushCondition({
      type: model.type,
      filter: model.filter,
    });

    return {
      rules: resultRules,
      rulesOperator: model.operator ?? 'AND',
    };
  }

  private matchesRuleGroup(
    normalizedCellValue: string,
    rawCellValue: string,
    activeRules: FilterRule[],
  ): boolean {
    if (activeRules.length === 0) {
      return false;
    }

    if (this.rulesOperator === 'OR') {
      return activeRules.some((rule) =>
        this.matchesRule(normalizedCellValue, rawCellValue, rule),
      );
    }

    return activeRules.every((rule) =>
      this.matchesRule(normalizedCellValue, rawCellValue, rule),
    );
  }

  private mapAgTextTypeToRuleType(type?: string): TextMatchType | null {
    switch (type) {
      case 'contains':
      case 'notContains':
      case 'equals':
      case 'notEqual':
      case 'startsWith':
      case 'endsWith':
        return type;
      default:
        return null;
    }
  }

  private parseLegacyRawText(raw: string): string[] {
    return raw
      .split(/[\n,]+/)
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
  }

  private matchesRule(
    normalizedCellValue: string,
    _cellValue: string,
    rule: FilterRule,
  ): boolean {
    const normalizedTextValue = rule.value.toLowerCase().trim();
    if (normalizedTextValue.length === 0) {
      return false;
    }

    switch (rule.matchType) {
      case 'contains':
        return normalizedCellValue.includes(normalizedTextValue);
      case 'notContains':
        return !normalizedCellValue.includes(normalizedTextValue);
      case 'equals':
        return normalizedCellValue === normalizedTextValue;
      case 'notEqual':
        return normalizedCellValue !== normalizedTextValue;
      case 'startsWith':
        return normalizedCellValue.startsWith(normalizedTextValue);
      case 'endsWith':
        return normalizedCellValue.endsWith(normalizedTextValue);
      default:
        return normalizedCellValue.includes(normalizedTextValue);
    }
  }

  private getCellValue(node: IRowNode): string {
    const data = node.data as Record<string, unknown> | undefined;
    if (data && this.fieldName in data) {
      const directValue = data[this.fieldName];
      if (directValue !== undefined && directValue !== null) {
        return String(directValue);
      }
    }

    const value = this.filterParams.getValue(node);
    if (value === undefined || value === null) {
      return '';
    }

    return String(value);
  }

  private getUniqueOptions(): string[] {
    const values = new Set<string>();

    const collectValue = (node: IRowNode) => {
      if (!node.data) {
        return;
      }

      const value = this.getCellValue(node);
      if (value !== '') {
        values.add(value);
      }
    };

    this.filterParams.api.forEachLeafNode(collectValue);

    if (values.size === 0) {
      this.filterParams.api.forEachNode(collectValue);
    }

    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }

  private renderOptions() {
    const allOptions = this.getUniqueOptions();
    this.optionsContainer.innerHTML = '';

    if (allOptions.length === 0) {
      const emptyMessage = document.createElement('div');
      emptyMessage.textContent = 'No values in grid';
      emptyMessage.style.fontSize = '12px';
      emptyMessage.style.color = 'var(--ag-secondary-foreground-color, #888)';
      this.optionsContainer.appendChild(emptyMessage);
      return;
    }

    allOptions.forEach((option) => {
      const label = document.createElement('label');
      label.style.display = 'flex';
      label.style.alignItems = 'center';
      label.style.gap = '8px';
      label.style.cursor = 'pointer';
      label.style.fontSize = '13px';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.value = option;
      input.checked = this.selectedOptions.has(option);

      label.appendChild(input);
      label.appendChild(document.createTextNode(option));
      this.optionsContainer.appendChild(label);
    });
  }

  private renderRules() {
    this.rulesContainer.innerHTML = '';

    this.rules.forEach((rule) => {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.gap = '6px';

      const conditionSelect = document.createElement('select');
      conditionSelect.className = 'ag-input-field-input ag-text-field-input';
      conditionSelect.style.minWidth = '120px';
      conditionSelect.style.padding = '6px';

      MATCH_OPTIONS.forEach((option) => {
        const optionEl = document.createElement('option');
        optionEl.value = option.value;
        optionEl.text = option.label;
        conditionSelect.appendChild(optionEl);
      });

      conditionSelect.value = rule.matchType;
      conditionSelect.addEventListener('change', (event) => {
        rule.matchType = (event.target as HTMLSelectElement)
          .value as TextMatchType;
        this.filterChangedCallback();
      });

      const valueInput = document.createElement('input');
      valueInput.type = 'text';
      valueInput.value = rule.value;
      valueInput.placeholder = 'Value';
      valueInput.className = 'ag-input-field-input ag-text-field-input';
      valueInput.style.flex = '1';
      valueInput.style.minWidth = '120px';
      valueInput.style.padding = '6px';
      valueInput.addEventListener('input', (event) => {
        rule.value = (event.target as HTMLInputElement).value;
        this.filterChangedCallback();
      });

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.textContent = 'x';
      removeButton.title = 'Remove rule';
      removeButton.className = 'ag-standard-button';
      removeButton.style.padding = '6px 8px';
      removeButton.style.cursor = 'pointer';
      removeButton.disabled = this.rules.length === 1;
      removeButton.addEventListener('click', () => {
        this.rules = this.rules.filter((item) => item.id !== rule.id);
        if (this.rules.length === 0) {
          this.rules = [this.createRule()];
        }
        this.renderRules();
        this.filterChangedCallback();
      });

      row.appendChild(conditionSelect);
      row.appendChild(valueInput);
      row.appendChild(removeButton);

      this.rulesContainer.appendChild(row);
    });
  }
}
